import { createSelector } from 'reselect';
import {
  CollectionOrder,
  DeliveryOrder,
  DeliveryType,
  GatewayPaymentAccount,
  Geo,
  OrderBase,
  PaymentAccount,
  PickupLocationType
} from '../Api';
import { BLANK } from '../constants/Constants';
import { basketLogger } from '../logger';
import {
  getBasketItemsBridge,
  getPickupLocationIdOrDefault,
  getPickupLocationTypeOrDefault,
  getSelectedRequestedTimeOrDefault,
  getSelectedPaymentOrDefault
} from '../redux/basket/selectors';
import { getRestaurantDetails, GetRestaurantProps, getRestaurantSummary } from './menuSelectors';
import * as paymentSelectors from './paymentSelectors';

type BasketProps = {
  menuId: number;
};

//#region HELPERS

/**
 * Round number to specified decimal numbers. default: 2
 * round(1.005) === 1.01
 * @param value number to round
 * @param decimals decimal places
 */
function round(value: number, decimals: number = 2) {
  // @ts-ignore
  return +(Math.round(value + 'e+' + decimals) + 'e-' + decimals);
}

const getAppConfig = (state: WebOrder.State) =>
  state.flipdish.AppConfig &&
  state.flipdish.AppConfig.status === 'success' &&
  state.flipdish.AppConfig.data;

const getUserConfig = (state: WebOrder.State) =>
  state.flipdish.Customer &&
  state.flipdish.Customer.status === 'success' &&
  state.flipdish.Customer.data;

//#endregion

//#region Order Capacity feature flag

export const getOrderCapacityFeatureFlag = createSelector<
  WebOrder.State,
  ReturnType<typeof getAppConfig>,
  boolean
>([getAppConfig], (config) => {
  return config ? config.Features.includes('order-capacity') : false;
});

//#endregion

//#region ITEM_TOTAL
export const getItemTotal = createSelector<
  WebOrder.State,
  BasketProps,
  ReturnType<typeof getBasketItemsBridge>,
  number
>([(state, props) => getBasketItemsBridge(state)], (basketItems) => {
  let total = 0;
  if (basketItems) {
    total = basketItems.reduce((agg, item) => {
      return (
        agg + item.Item.Price + item.Options.map((o) => o.Price).reduce((oagg, o) => oagg + o, 0)
      );
    }, 0);
  }

  return total;
});
//#endregion

//#region ITEM_TAX_TOTAL

export const getDeliveryFeeTaxAmount = (state: WebOrder.State, props: GetRestaurantProps) => {
  const Restaurant = getRestaurantDetails(state, props);
  let total = 0;
  if (Restaurant && Restaurant.DeliveryFeeTaxAmount) {
    total = Restaurant.DeliveryFeeTaxAmount;
  }

  return total;
};

export const getItemTaxTotal = createSelector<
  WebOrder.State,
  BasketProps,
  ReturnType<typeof getBasketItemsBridge>,
  number
>([(state, props) => getBasketItemsBridge(state)], (basketItems) => {
  let total = 0;
  if (basketItems) {
    total = basketItems.reduce((agg, item) => {
      return (
        agg +
        item.Item.TaxValue +
        item.Options.map((o) => o.TaxValue).reduce((oagg, o) => oagg + o, 0)
      );
    }, 0);
  }

  return total;
});
//#endregion

//#region PAYMENT_OPTION
const getPaymentOptions = paymentSelectors.getAvailablePayments;

const getSelectedPaymentOption = paymentSelectors.getSelectedPayment;

export const getPaymentOption = createSelector<
  WebOrder.State,
  GetRestaurantProps,
  ReturnType<typeof getSelectedPaymentOption>,
  ReturnType<typeof getPaymentOptions>,
  ReturnType<typeof getRestaurantDetails>,
  PaymentAccount | undefined
>(
  [getSelectedPaymentOption, getPaymentOptions, getRestaurantDetails],
  (selectedPayment, payments, restaurant) => {
    if (!payments || !payments.length || !restaurant) {
      return undefined;
    }

    if (selectedPayment) {
      const tryKeepSameRefObject = payments.find(
        (p) => p.PaymentAccountId === selectedPayment.PaymentAccountId
      );
      return tryKeepSameRefObject || selectedPayment;
    }

    let defaultPayment = payments.find((p) => p.IsDefaultPaymentMethod);
    // just in case
    if (!defaultPayment && payments[0]) {
      defaultPayment = payments[0];
    }

    if (
      defaultPayment.PaymentAccountType === 'CashPaymentAccount' &&
      !restaurant.AcceptCashOrders
    ) {
      defaultPayment = payments.find((p) => p.PaymentAccountType !== 'CashPaymentAccount');
    }

    return defaultPayment;
  }
);

//#endregion

//#region PAYMENT_FEE_TOTAL
export const getPaymentFee = createSelector<
  WebOrder.State,
  GetRestaurantProps & BasketProps,
  ReturnType<typeof getPaymentOption>,
  ReturnType<typeof getRestaurantDetails>,
  ReturnType<typeof getPickupLocationTypeOrDefault>,
  number,
  number
>(
  [getPaymentOption, getRestaurantDetails, getPickupLocationTypeOrDefault, getItemTotal],
  (SelectedPaymentOption, Restaurant, pickupLocation, basketTotal) => {
    let total = 0;
    if (SelectedPaymentOption && Restaurant && basketTotal) {
      const isCashFee =
        SelectedPaymentOption.PaymentAccountType === 'CashPaymentAccount' ||
        SelectedPaymentOption.PaymentAccountType === GatewayPaymentAccount.Ideal ||
        SelectedPaymentOption.PaymentAccountType === GatewayPaymentAccount.Bancontact;

      const isLocationServiceFee = pickupLocation === PickupLocationType.TableService;

      let fee: number;
      if (
        isLocationServiceFee &&
        'LocationServiceFixedFeeCashOrdersChargedToCustomerIncludingVat' in Restaurant
      ) {
        if (isCashFee) {
          fee =
            Restaurant.LocationServiceFixedFeeCashOrdersChargedToCustomerIncludingVat +
            (basketTotal *
              Restaurant.LocationServicePercentFeeCashOrdersChargedToCustomerIncludingVat) /
              100;
        } else {
          fee =
            Restaurant.LocationServiceFixedFeeCardOrdersChargedToCustomerIncludingVat +
            (basketTotal *
              Restaurant.LocationServicePercentFeeCardOrdersChargedToCustomerIncludingVat) /
              100;
        }
      } else if (isCashFee) {
        fee =
          Restaurant.FixedFeeCashOrdersChargedToCustomerIncludingVat +
          (basketTotal * Restaurant.PercentFeeCashOrdersChargedToCustomerIncludingVat) / 100;
      } else {
        fee =
          Restaurant.FixedFeeCardOrdersChargedToCustomerIncludingVat +
          (basketTotal * Restaurant.PercentFeeCardOrdersChargedToCustomerIncludingVat) / 100;
      }

      total = isNaN(fee) ? 0 : fee;
    }

    return total;
  }
);
//#endregion

//#region DELIVERY_FEE_TOTAL
export const getDeliveryFee = createSelector<
  WebOrder.State,
  GetRestaurantProps,
  ReturnType<typeof getRestaurantDetails>,
  number
>([(state, props) => getRestaurantDetails(state, props)], (Restaurant) => {
  let total = 0;
  if (Restaurant) {
    if ('DeliveryFee' in Restaurant) {
      total = Restaurant.DeliveryFee;
    }
  }

  return total;
});
//#endregion

//#region VOUCHER

export const getVoucher = (state: WebOrder.State) => {
  return state.basket.voucher;
};

//#endregion

//#region VOUCHER_TOTAL

export const getVoucherTotal = createSelector<
  WebOrder.State,
  BasketProps,
  ReturnType<typeof getVoucher>,
  number
>([getVoucher], (voucher) => {
  if (voucher && voucher.code && voucher.amount) {
    return voucher.amount;
  }
  return 0;
});

//#endregion

//#region TAX_TYPE
export const getTaxType = createSelector<
  WebOrder.State,
  GetRestaurantProps,
  ReturnType<typeof getRestaurantDetails>,
  number
>([(state, props) => getRestaurantDetails(state, props)], (Restaurant) => {
  let taxType = 0;
  if (Restaurant) {
    if ('TaxType' in Restaurant) {
      taxType = Restaurant.TaxType;
    }
  }

  return taxType;
});
//#endregion

//#region TOTAL
type TotalProps =
  | {
      menuId: number;
      vrId: number;
      deliveryType: 'collection';
      prId: number;
    }
  | {
      menuId: number;
      vrId: number;
      deliveryType: 'delivery';
      deliveryLocationId: number;
    };

export const getTotal = createSelector<
  WebOrder.State,
  TotalProps,
  number,
  number,
  number,
  number,
  number,
  number,
  number
>(
  [
    (state, props) => getDeliveryFeeTaxAmount(state, props),
    (state, props) => getTaxType(state, props),
    (state, props) => getItemTotal(state, props),
    (state, props) => getItemTaxTotal(state, props),
    (state, props) => getPaymentFee(state, props),
    (state, props) => getDeliveryFee(state, props)
  ],
  (deliveryFeeTaxAmount, taxType, itemTotal, itemTaxTotal, paymentFeeTotal, deliveryFeeTotal) => {
    const total =
      itemTotal +
      paymentFeeTotal +
      deliveryFeeTotal +
      deliveryFeeTaxAmount +
      (taxType === 1 ? itemTaxTotal : 0);

    if (total) {
      basketLogger.debug('TOTAL', total);
    }

    return total;
  }
);

//#endregion

//#region TIPS_ENABLED

const getAppTipsEnabled = (state: WebOrder.State) => {
  const config = getAppConfig(state);
  if (config) {
    return config.TipsEnabled;
  }
  return false;
};

const getRestaurantTipsEnabled = (state: WebOrder.State, props: GetRestaurantProps) => {
  const config = getRestaurantDetails(state, props);
  if (config) {
    return config.TipsEnabled;
  }
  return false;
};

const getUserTipsEnabled = (state: WebOrder.State) => {
  const config = getUserConfig(state);
  if (config) {
    return config.DisplayTipOption;
  }
  return false;
};

export const getTipEnabled = createSelector<
  WebOrder.State,
  TotalProps,
  ReturnType<typeof getAppTipsEnabled>,
  ReturnType<typeof getRestaurantTipsEnabled>,
  ReturnType<typeof getUserTipsEnabled>,
  boolean
>(
  [
    (state, props) => getAppTipsEnabled(state),
    (state, props) => getRestaurantTipsEnabled(state, props),
    (state, props) => getUserTipsEnabled(state)
  ],
  (appTipsEnabled, restaurantTipsEnabled, userTipsEnabled) => {
    return appTipsEnabled && restaurantTipsEnabled && userTipsEnabled;
  }
);
//#endregion

//#region TIPS

const getUserDefaultTipPercentage = (state: WebOrder.State) => {
  const config = getUserConfig(state);
  if (config) {
    return config.DefaultTipPercentage;
  }
  return 0;
};

const getSelectedTipOrDefault = (state: WebOrder.State) => {
  const tip = state.basket.tip;
  // for new-comers: 0 (zero) is falsy so if we have any number in state use that else default
  if (typeof tip === 'number') {
    return tip;
  }
  return getUserDefaultTipPercentage(state);
};

const getCanTipOnSelectedPayment = (state: WebOrder.State) => {
  const payment = getSelectedPaymentOrDefault(state);

  if (payment && payment.SupportsSubmitOrderTips) {
    return true;
  }
  return false;
};

export const getTipTotal = createSelector<
  WebOrder.State,
  TotalProps,
  boolean,
  ReturnType<typeof getCanTipOnSelectedPayment>,
  ReturnType<typeof getSelectedTipOrDefault>,
  number,
  number
>(
  [
    getTipEnabled,
    (state, props) => getCanTipOnSelectedPayment(state),
    (state, props) => getSelectedTipOrDefault(state),
    getTotal
  ],
  (tipsEnabled, canTipOnSelectedPayment, selectedOrDefaultTip, total) => {
    if (!tipsEnabled || !canTipOnSelectedPayment) {
      return 0;
    }

    const tipsTotal = round(total * (selectedOrDefaultTip / 100));

    if (tipsTotal) {
      basketLogger.debug('TIPS TOTAL', tipsTotal, total);
    }

    return tipsTotal;
  }
);
//#endregion

//#region TOTAL_TO_PAY

export const getTotalToPay = createSelector<
  WebOrder.State,
  TotalProps,
  number,
  number,
  number,
  number
>([getTotal, getTipTotal, getVoucherTotal], (totalAmount, tipsTotal, voucherTotal) => {
  const total = round(totalAmount + tipsTotal + voucherTotal);

  if (total) {
    basketLogger.debug('TOTAL_TO_PAY (INC.TIPS)', total, totalAmount, tipsTotal, voucherTotal);
  }

  return total;
});

//#endregion

//#region BASKET_VALIDATION
type BasketError =
  | 'empty_basket'
  | 'missing_restaurant_details'
  | 'below_minimum_amount'
  | 'missing_pre_order_time';
export const getBasketValidationError = createSelector<
  WebOrder.State,
  OrderPayloadProps,
  ReturnType<typeof getBasketItemsBridge>,
  ReturnType<typeof getRestaurantDetails>,
  number,
  ReturnType<typeof getSelectedRequestedTimeOrDefault>,
  BasketError | undefined
>(
  [
    (state, props) => getBasketItemsBridge(state),
    getRestaurantDetails,
    getItemTotal,
    (state, props) => getSelectedRequestedTimeOrDefault(state)
  ],
  (basketItems, restaurant, itemTotal, requestedTime) => {
    if (!basketItems || (basketItems && !basketItems.length)) {
      return 'empty_basket';
    }

    // don't work w cached data
    if (!restaurant || !restaurant.FromAPI) {
      return 'missing_restaurant_details';
    }

    if (restaurant.MinimumOrder > itemTotal) {
      return 'below_minimum_amount';
    }

    const preOrderEnabled = restaurant.AcceptPreOrders && restaurant.PreOrderOptions;
    const preOrderExplicit = preOrderEnabled && restaurant.PreOrderOptions.RequireExplicitSelect;
    if (
      preOrderEnabled &&
      preOrderExplicit &&
      (!requestedTime || requestedTime === BLANK) // backend supports requestedTime="" sometimes 😱
    ) {
      return 'missing_pre_order_time';
    }

    return undefined;
  }
);
//#endregion

//#region ORDER_PAYLOAD
export type OrderPayloadProps =
  | {
      menuId: number;
      menuVersionGuid: string;
      vrId: number;
      deliveryType: 'collection';
      prId: number;
    }
  | {
      menuId: number;
      vrId: number;
      menuVersionGuid: string;
      deliveryType: 'delivery';
      deliveryLocationId: number;
    };

const getOrderPayloadBase = createSelector<
  WebOrder.State,
  OrderPayloadProps,
  OrderPayloadProps['vrId'],
  OrderPayloadProps['menuId'],
  OrderPayloadProps['menuVersionGuid'],
  OrderPayloadProps['deliveryType'],
  ReturnType<typeof getBasketItemsBridge>,
  ReturnType<typeof getSelectedRequestedTimeOrDefault>,
  ReturnType<typeof getTipTotal>,
  ReturnType<typeof getPaymentOption>,
  ReturnType<typeof getRestaurantSummary>,
  string,
  OrderBase
>(
  [
    (state, props) => props.vrId,
    (state, props) => props.menuId,
    (state, props) => props.menuVersionGuid,
    (state, props) => props.deliveryType,
    (state, props) => getBasketItemsBridge(state),
    (state, props) => getSelectedRequestedTimeOrDefault(state),
    (state, props) => getTipTotal(state, props),
    (state, props) => getPaymentOption(state, props),
    (state, props) => getRestaurantSummary(state, props),
    (state, props) => state.flipdish.Config.DeviceId
  ],
  (
    vrId,
    menuId,
    menuVersionGuid,
    deliveryType,
    basketItems,
    requestedTime,
    tipAmount,
    paymentOption,
    restaurant,
    deviceId
  ) => {
    if (!vrId || !menuId || !menuVersionGuid || !basketItems.length || !restaurant) {
      return undefined;
    }

    const orderBase = {
      VirtualRestaurantId: vrId,
      deviceId,
      OrderItemVms: basketItems.map((item) => ({
        MenuItemId: item.Item.MenuItemId,
        MenuItemOptionSetItemIds: item.Options.map((option) => option.MenuItemOptionSetItemId)
      })),
      TipAmount: tipAmount,
      RequestedDeliveryOrPickupTimeUtc: requestedTime,
      MenuId: menuId,
      VersionGuid: menuVersionGuid,
      DeliveryType: deliveryType === 'collection' ? DeliveryType.Collection : DeliveryType.Delivery,
      PaymentAccountId:
        paymentOption && paymentOption.PaymentAccountId > 0
          ? paymentOption.PaymentAccountId
          : undefined,
      // needed 4 collection and delivery
      PhysicalRestaurantId: restaurant.PhysicalRestaurantId
    };

    return orderBase;
  }
);

export type OrderPayload = ReturnType<typeof getOrderPayload>;

export const getOrderPayload = createSelector<
  WebOrder.State,
  OrderPayloadProps,
  ReturnType<typeof getOrderPayloadBase>,
  number,
  ReturnType<typeof getBasketValidationError>,
  PickupLocationType,
  ReturnType<typeof getPickupLocationIdOrDefault>,
  Geo,
  DeliveryOrder | CollectionOrder | BasketError
>(
  [
    getOrderPayloadBase,
    (state, props) => (props.deliveryType === 'collection' ? props.prId : props.deliveryLocationId),
    getBasketValidationError,
    (state, props) => getPickupLocationTypeOrDefault(state),
    (state, props) => getPickupLocationIdOrDefault(state),
    (state, props) => state.flipdish.Location.geo
  ],
  (orderBase, prOrDlId, basketError, pickupLocationType, tableNumber, userLocation) => {
    if (basketError) {
      basketLogger.debug('SELECTOR ORDER_PAYLOAD_ERROR', basketError);
      return basketError;
    }
    if (!orderBase) {
      return undefined;
    }

    let orderPayload: DeliveryOrder | CollectionOrder;
    switch (orderBase.DeliveryType) {
      case DeliveryType.Collection:
        orderPayload = {
          ...orderBase,
          DeliveryType: DeliveryType.Collection,
          PhysicalRestaurantId: prOrDlId,
          PickupLocationType: pickupLocationType
        };
        if (typeof tableNumber === 'number') {
          orderPayload.PickupLocationId = tableNumber;
        }
        break;

      case DeliveryType.Delivery:
        orderPayload = {
          ...orderBase,
          DeliveryType: DeliveryType.Delivery,
          DeliveryLocationId: prOrDlId,
          UserLocation: userLocation
        };
        break;
    }

    basketLogger.debug('SELECTOR ORDER_PAYLOAD', orderPayload);
    return orderPayload;
  }
);

//#endregion
